import { useEffect, useMemo, useState } from "react";
import { OrderTypeTag } from "../components/OrderTypeTag";
import api, { formatError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { PageHeader } from "../components/GradientCard";
import { useGlobalViewMode } from "../components/ViewModeToggle";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import { Search, Users, Plus, Trash, ClipboardList, Pencil, UserPlus, Calculator, ScrollText, Power, UserX, AlertTriangle, BookOpen } from "lucide-react";
import ServiceOrderCalculation from "./ServiceOrderCalculation";
import LogViewerDialog from "../components/LogViewerDialog";

// Status helper — Red: no employees, Orange: trainer_free_date within 2 days, Green: mapped
const computeOrderStatus = (order, ces) => {
  const emps = order.contractual_employees || [];
  const active = emps.filter((e) => e.is_active !== false);
  if (active.length === 0) return { color: "red", label: "No Employee" };
  // Check if any linked directory employee is INACTIVE
  const hasInactiveDir = active.some((e) => {
    if (!e.directory_id) return false;
    const dir = ces.find((c) => c.id === e.directory_id);
    return dir && dir.is_active === false;
  });
  // Check if trainer_free_date is within next 2 days
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const twoDays = new Date(today); twoDays.setDate(twoDays.getDate() + 2);
  const isEnding = active.some((e) => {
    if (!e.trainer_free_date) return false;
    const d = new Date(e.trainer_free_date);
    return !isNaN(d.getTime()) && d <= twoDays && d >= today;
  });
  if (hasInactiveDir) return { color: "orange", label: "No Active User" };
  if (isEnding) return { color: "orange", label: "Ending Soon" };
  return { color: "green", label: "Mapped" };
};

const STATUS_DOT = {
  red: "bg-rose-500 ring-rose-100",
  orange: "bg-amber-500 ring-amber-100",
  green: "bg-emerald-500 ring-emerald-100",
};

const EMPTY_ORDER_EMP = {
  name: "", email: "", mobile: "", pan: "", aadhaar: "",
  job_type: "",
  rate_type: "per_day", rate_amount: 0, rate_per_session: 0,
  travel_needed: false, travel_mode: "fixed", travel_amount: 0, travel_remark: "",
  service_order_no: "", service_order_remark: "",
  sessions_per_day_committed: 0,
  school_from: "", school_to: "",
  travel_start_date: "", trainer_reach_date: "", trainer_free_date: "",
  remark: "",
  directory_id: "",
};

const EMPTY_CE = {
  name: "", email: "", mobile: "", pan: "", aadhaar: "",
  address: "", bank_account_no: "", ifsc: "",
  is_account_added: false, company_account_name: "",
  job_type: "",
  rate_type: "per_day", rate_amount: 0, rate_per_session: 0,
  travel_needed: false,
  travel_mode: "fixed", travel_amount: 0,
  travel_remark: "", travel_variable_remark: "",
  remark: "",
};

export default function ServiceOrderManagement({ embedded = false } = {}) {
  const { user } = useAuth();
  const [viewMode] = useGlobalViewMode();
  const isMasterAdmin = user?.role === "master_admin";
  const [tab, setTab] = useState("orders");

  // Log viewer dialog
  const [logViewer, setLogViewer] = useState({ open: false, title: "", endpoint: "" });
  const openOrderLogs = (o) => setLogViewer({ open: true, title: `Logs · ${o.order_id || "Service Order"}`, endpoint: `/service-orders/${o.id}/logs` });
  const openCELogs = (c) => setLogViewer({ open: true, title: `Logs · ${c.name}`, endpoint: `/service-order/contractual-employees/${c.id}/logs` });

  // Mark Inactive dialog
  const [markInactive, setMarkInactive] = useState(null); // { emp, did_sessions, reason }

  // Direct For Invoice dialog
  const [directInvoice, setDirectInvoice] = useState(null);
  const [savingDirect, setSavingDirect] = useState(false);
  const submitDirectInvoice = async () => {
    if (!directInvoice) return;
    const amount = Number(directInvoice.amount || 0);
    if (!directInvoice.month_key || amount <= 0) { toast.error("Month and a positive amount are required"); return; }
    setSavingDirect(true);
    try {
      await api.post(`/service-orders/${directInvoice.order.id}/direct-invoice`, {
        month_key: directInvoice.month_key,
        amount,
        notes: directInvoice.notes || "",
      });
      toast.success("Sent to Accounts · Service Calc tab");
      setDirectInvoice(null);
    } catch (e) { toast.error(formatError(e)); }
    finally { setSavingDirect(false); }
  };

  // Orders
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all"); // all|red|orange|green
  const [detailOrder, setDetailOrder] = useState(null);
  const [empForm, setEmpForm] = useState(EMPTY_ORDER_EMP);
  const [showEmpForm, setShowEmpForm] = useState(false);
  const [saving, setSaving] = useState(false);

  // Contractual Employees (master directory)
  const [ces, setCEs] = useState([]);
  const [ceLoading, setCELoading] = useState(true);
  const [ceSearch, setCESearch] = useState("");
  const [ceActiveFilter, setCEActiveFilter] = useState("active"); // all|active|inactive
  const [ceJobFilter, setCEJobFilter] = useState("");
  const [ceForm, setCEForm] = useState(EMPTY_CE);
  const [editingCE, setEditingCE] = useState(null);
  const [showCEForm, setShowCEForm] = useState(false);
  const [ceSaving, setCESaving] = useState(false);

  const loadOrders = () => {
    setLoading(true);
    api.get("/service-orders")
      .then(({ data }) => setOrders(data || []))
      .catch((e) => toast.error(formatError(e)))
      .finally(() => setLoading(false));
  };

  const loadCEs = () => {
    setCELoading(true);
    api.get("/service-order/contractual-employees")
      .then(({ data }) => setCEs(data || []))
      .catch((e) => toast.error(formatError(e)))
      .finally(() => setCELoading(false));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    loadOrders();
    loadCEs();
  }, []);

  const filteredOrders = useMemo(() => {
    const q = search.toLowerCase().trim();
    let list = orders;
    if (q) {
      list = list.filter((o) => [o.order_id, o.client_name, o.product_service, o.sale_associate_name]
        .some((v) => String(v ?? "").toLowerCase().includes(q)));
    }
    if (statusFilter !== "all") {
      list = list.filter((o) => computeOrderStatus(o, ces).color === statusFilter);
    }
    return list;
  }, [orders, search, statusFilter, ces]);

  const uniqueJobTypes = useMemo(() => {
    const set = new Set();
    ces.forEach((c) => { if (c.job_type) set.add(c.job_type); });
    return Array.from(set).sort();
  }, [ces]);

  const filteredCEs = useMemo(() => {
    const q = ceSearch.toLowerCase().trim();
    let list = ces;
    if (ceActiveFilter === "active") list = list.filter((c) => c.is_active !== false);
    else if (ceActiveFilter === "inactive") list = list.filter((c) => c.is_active === false);
    if (ceJobFilter) list = list.filter((c) => c.job_type === ceJobFilter);
    if (q) {
      list = list.filter((c) => [c.name, c.email, c.mobile, c.pan, c.aadhaar, c.job_type]
        .some((v) => String(v ?? "").toLowerCase().includes(q)));
    }
    return list;
  }, [ces, ceSearch, ceActiveFilter, ceJobFilter]);

  const refreshDetail = async (id) => {
    const { data } = await api.get("/service-orders");
    setOrders(data || []);
    const fresh = (data || []).find((o) => o.id === id);
    if (fresh) setDetailOrder(fresh);
  };

  const addOrderEmployee = async () => {
    if (!empForm.name.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      await api.post(`/service-orders/${detailOrder.id}/employees`, empForm);
      toast.success("Contractual employee added");
      setEmpForm(EMPTY_ORDER_EMP);
      setShowEmpForm(false);
      refreshDetail(detailOrder.id);
    } catch (e) { toast.error(formatError(e)); }
    finally { setSaving(false); }
  };

  const removeOrderEmployee = async (empId) => {
    if (!window.confirm("Remove this employee?")) return;
    try {
      await api.delete(`/service-orders/${detailOrder.id}/employees/${empId}`);
      toast.success("Removed");
      refreshDetail(detailOrder.id);
    } catch (e) { toast.error(formatError(e)); }
  };

  // ---- Contractual Employee master CRUD ----
  const openNewCE = () => { setEditingCE(null); setCEForm(EMPTY_CE); setShowCEForm(true); };
  const openEditCE = (c) => {
    setEditingCE(c);
    setCEForm({
      name: c.name || "", email: c.email || "", mobile: c.mobile || "",
      pan: c.pan || "", aadhaar: c.aadhaar || "",
      address: c.address || "",
      bank_account_no: c.bank_account_no || "",
      ifsc: c.ifsc || "",
      is_account_added: !!c.is_account_added,
      company_account_name: c.company_account_name || "",
      job_type: c.job_type || "",
      rate_type: c.rate_type || "per_day",
      rate_amount: c.rate_amount || 0,
      rate_per_session: c.rate_per_session || 0,
      travel_needed: !!c.travel_needed,
      travel_mode: c.travel_mode || "fixed",
      travel_amount: c.travel_amount || 0,
      travel_remark: c.travel_remark || "",
      travel_variable_remark: c.travel_variable_remark || "",
      remark: c.remark || "",
    });
    setShowCEForm(true);
  };

  const saveCE = async () => {
    if (!ceForm.name.trim()) { toast.error("Name is required"); return; }
    setCESaving(true);
    try {
      if (editingCE) {
        await api.put(`/service-order/contractual-employees/${editingCE.id}`, ceForm);
        toast.success("Employee updated");
      } else {
        await api.post(`/service-order/contractual-employees`, ceForm);
        toast.success("Employee added");
      }
      setShowCEForm(false);
      setEditingCE(null);
      setCEForm(EMPTY_CE);
      loadCEs();
    } catch (e) { toast.error(formatError(e)); }
    finally { setCESaving(false); }
  };

  const deleteCE = async (id) => {
    if (!window.confirm("Delete this contractual employee?")) return;
    try {
      await api.delete(`/service-order/contractual-employees/${id}`);
      toast.success("Deleted");
      loadCEs();
    } catch (e) { toast.error(formatError(e)); }
  };

  const toggleCEActive = async (c) => {
    try {
      await api.patch(`/service-order/contractual-employees/${c.id}/toggle-active`);
      toast.success(c.is_active === false ? "Activated" : "Marked Inactive");
      loadCEs();
    } catch (e) { toast.error(formatError(e)); }
  };

  const confirmMarkInactive = async () => {
    if (!markInactive) return;
    try {
      await api.patch(`/service-orders/${detailOrder.id}/employees/${markInactive.emp.id}/mark-inactive`, {
        did_sessions: !!markInactive.did_sessions,
        reason: markInactive.reason || "",
      });
      toast.success("Employee marked inactive for this order");
      const continueToCalc = !!markInactive.did_sessions;
      setMarkInactive(null);
      await refreshDetail(detailOrder.id);
      if (continueToCalc) {
        // Switch dialog to Calculate tab visually — user can compute the salary
        toast.message("Salary calculation pending — opening Calculate tab");
        // Trigger tab change via a tiny DOM click since defaultValue can't be controlled here
        setTimeout(() => {
          const btn = document.querySelector('[data-testid="so-tab-calculate"]');
          if (btn) btn.click();
        }, 200);
      }
    } catch (e) { toast.error(formatError(e)); }
  };

  return (
    <div className={embedded ? "space-y-6" : "max-w-[1400px] space-y-6"}>
      {!embedded && (
        <PageHeader
          moduleKey="sales_orders"
          tone="indigo"
          icon={BookOpen}
          eyebrow="Workspace"
          title="Service Order Management"
          subtitle="Manage Service orders and the contractual employees deployed against them."
        />
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className={embedded ? "bg-slate-100/70 dark:bg-slate-800/50 p-1 rounded-lg gap-1 h-auto flex-wrap" : ""}>
          <TabsTrigger value="orders" data-testid="som-tab-orders">
            <ClipboardList size={13} className="mr-1.5" /> Service Orders · {orders.length}
          </TabsTrigger>
          <TabsTrigger value="employees" data-testid="som-tab-employees">
            <Users size={13} className="mr-1.5" /> Contractual Employees · {ces.length}
          </TabsTrigger>
        </TabsList>

        {/* ============================ SERVICE ORDERS TAB ============================ */}
        <TabsContent value="orders" className="space-y-4 mt-4">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative max-w-md flex-1 min-w-[220px]">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search order, client, sale associate…" className="pl-9 h-10" data-testid="so-search" />
            </div>
            <Tabs value={statusFilter} onValueChange={setStatusFilter} className="w-auto" data-testid="so-status-filter">
              <TabsList>
                <TabsTrigger value="all" data-testid="so-status-all">All</TabsTrigger>
                <TabsTrigger value="green" data-testid="so-status-green"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mr-1" /> Mapped</TabsTrigger>
                <TabsTrigger value="orange" data-testid="so-status-orange"><span className="w-1.5 h-1.5 rounded-full bg-amber-500 mr-1" /> Attention</TabsTrigger>
                <TabsTrigger value="red" data-testid="so-status-red"><span className="w-1.5 h-1.5 rounded-full bg-rose-500 mr-1" /> No Employee</TabsTrigger>
              </TabsList>
            </Tabs>
            <Badge variant="outline" className="ml-auto text-[11px]">{filteredOrders.length} of {orders.length}</Badge>
          </div>

          {loading ? (
            <div className="pk-card p-12 text-center text-slate-400">Loading…</div>
          ) : filteredOrders.length === 0 ? (
            <div className="pk-card p-12 text-center text-slate-400" data-testid="so-empty">
              No service orders yet. Create an order with <strong>Order Type = Service</strong> in Order Management to see it here.
            </div>
          ) : viewMode === "card" ? (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="so-cards">
              {filteredOrders.map((o) => {
                const status = computeOrderStatus(o, ces);
                return (
                  <div key={o.id} className="pk-card p-4 hover:shadow-md transition-shadow" data-testid={`so-card-${o.id}`}>
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="min-w-0 flex-1">
                        <div className="font-bold text-slate-900 truncate">{o.client_name || "—"}</div>
                        <div className="mono text-[10.5px] text-slate-500 truncate flex items-center gap-1.5">{o.order_id || "—"} <OrderTypeTag type="Service" testid={`so-type-card-${o.id}`} /></div>
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0">
                        <span className={`inline-block h-3 w-3 rounded-full ${STATUS_DOT[status.color]} ring-2`} title={status.label} data-status={status.color} />
                        {status.color !== "green" && <Badge className={`text-[9.5px] ${status.color === "red" ? "bg-rose-100 text-rose-800" : "bg-amber-100 text-amber-800"}`}>{status.label}</Badge>}
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-[11.5px] mb-2">
                      <div className="col-span-2"><div className="text-[9.5px] uppercase tracking-wider text-slate-500 font-semibold">Service</div><div className="text-slate-800 truncate">{o.product_service || "—"}</div></div>
                      <div><div className="text-[9.5px] uppercase tracking-wider text-slate-500 font-semibold">Value</div><div className="mono font-semibold text-slate-900">₹ {Number(o.total_value || 0).toLocaleString("en-IN")}</div></div>
                      <div><div className="text-[9.5px] uppercase tracking-wider text-slate-500 font-semibold">Employees</div><div><Badge variant="secondary" className="text-[10.5px]"><Users size={9} className="mr-1" /> {(o.contractual_employees || []).length}</Badge></div></div>
                    </div>
                    <div className="pt-2 border-t border-slate-100 flex flex-wrap gap-1.5 justify-end">
                      <Button size="sm" variant="outline" onClick={() => openOrderLogs(o)} className="h-7 text-[10.5px]" data-testid={`so-logs-card-${o.id}`} title="Logs"><ScrollText size={10} className="mr-1" /> Logs</Button>
                      <Button size="sm" variant="outline" className="h-7 text-[10.5px] text-violet-700 border-violet-200 hover:bg-violet-50" onClick={() => setDirectInvoice({ order: o, month_key: new Date().toISOString().slice(0,7), amount: "", notes: "" })} data-testid={`so-direct-card-${o.id}`}>Direct</Button>
                      <Button size="sm" onClick={() => { setDetailOrder(o); setShowEmpForm(false); }} className="h-7 text-[10.5px]" data-testid={`so-open-card-${o.id}`}>Open</Button>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="pk-card overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm responsive-table" data-testid="so-table">
                  <thead className="bg-slate-50/80 border-b">
                    <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500">
                      <th className="px-2 py-3 font-semibold w-10">●</th>
                      <th className="px-4 py-3 font-semibold">Order #</th>
                      <th className="px-4 py-3 font-semibold">Client</th>
                      <th className="px-4 py-3 font-semibold">Service</th>
                      <th className="px-4 py-3 font-semibold">Value</th>
                      <th className="px-4 py-3 font-semibold">Employees</th>
                      <th className="px-4 py-3 font-semibold text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredOrders.map((o) => {
                      const status = computeOrderStatus(o, ces);
                      return (
                      <tr key={o.id} className="border-b last:border-0 hover:bg-slate-50/60" data-testid={`so-row-${o.id}`}>
                        <td data-label="Status" className="px-2 py-3" title={status.label}>
                          <span className={`inline-block h-3 w-3 rounded-full ${STATUS_DOT[status.color]} ring-2`} data-testid={`so-status-${o.id}`} data-status={status.color} />
                        </td>
                        <td data-label="Order #" className="px-4 py-3 mono font-semibold text-[12px]"><span className="inline-flex items-center gap-1.5">{o.order_id || "—"} <OrderTypeTag type="Service" testid={`so-type-${o.id}`} /></span></td>
                        <td data-label="Client" className="px-4 py-3 font-medium text-slate-800">
                          {o.client_name || "—"}
                          {status.color !== "green" && (
                            <Badge className={`ml-2 text-[10px] ${status.color === "red" ? "bg-rose-100 text-rose-800" : "bg-amber-100 text-amber-800"}`}>
                              {status.label}
                            </Badge>
                          )}
                        </td>
                        <td data-label="Service" className="px-4 py-3 text-[12px] text-slate-600">{o.product_service || "—"}</td>
                        <td data-label="Value" className="px-4 py-3 mono">₹ {Number(o.total_value || 0).toLocaleString("en-IN")}</td>
                        <td data-label="Employees" className="px-4 py-3">
                          <Badge variant="secondary" className="text-[11px]">
                            <Users size={11} className="mr-1" /> {(o.contractual_employees || []).length}
                          </Badge>
                        </td>
                        <td data-label="Actions" className="px-4 py-3 text-right space-x-1">
                          <Button size="sm" variant="outline" onClick={() => openOrderLogs(o)} data-testid={`so-logs-${o.id}`} title="View Logs">
                            <ScrollText size={12} />
                          </Button>
                          <Button
                            size="sm" variant="outline"
                            className="text-violet-700 border-violet-200 hover:bg-violet-50"
                            onClick={() => setDirectInvoice({ order: o, month_key: new Date().toISOString().slice(0,7), amount: "", notes: "" })}
                            data-testid={`so-direct-${o.id}`}
                            title="Direct For Invoice (skip calc)"
                          >
                            Direct Invoice
                          </Button>
                          <Button size="sm" onClick={() => { setDetailOrder(o); setShowEmpForm(false); }} data-testid={`so-open-${o.id}`}>
                            Open
                          </Button>
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </TabsContent>

        {/* ============================ CONTRACTUAL EMPLOYEES TAB ============================ */}
        <TabsContent value="employees" className="space-y-4 mt-4">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative max-w-md flex-1 min-w-[220px]">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input value={ceSearch} onChange={(e) => setCESearch(e.target.value)} placeholder="Search by name, email, mobile, PAN…" className="pl-9 h-10" data-testid="ce-search" />
            </div>
            <Tabs value={ceActiveFilter} onValueChange={setCEActiveFilter} className="w-auto" data-testid="ce-active-filter">
              <TabsList>
                <TabsTrigger value="active" data-testid="ce-filter-active">Active</TabsTrigger>
                <TabsTrigger value="inactive" data-testid="ce-filter-inactive">Inactive</TabsTrigger>
                <TabsTrigger value="all" data-testid="ce-filter-all">All</TabsTrigger>
              </TabsList>
            </Tabs>
            {uniqueJobTypes.length > 0 && (
              <select
                value={ceJobFilter}
                onChange={(e) => setCEJobFilter(e.target.value)}
                className="h-8 border rounded text-[12px] px-2 bg-white"
                data-testid="ce-job-filter"
              >
                <option value="">All job types</option>
                {uniqueJobTypes.map((j) => <option key={j} value={j}>{j}</option>)}
              </select>
            )}
            <Badge variant="outline" className="text-[11px]">{filteredCEs.length} of {ces.length}</Badge>
            <Button size="sm" onClick={openNewCE} data-testid="ce-add-btn">
              <UserPlus size={13} className="mr-1" /> Add Contractual Employee
            </Button>
          </div>

          {ceLoading ? (
            <div className="pk-card p-12 text-center text-slate-400">Loading…</div>
          ) : filteredCEs.length === 0 ? (
            <div className="pk-card p-12 text-center text-slate-400" data-testid="ce-empty">
              No contractual employees yet. Click <strong>Add Contractual Employee</strong> to create one.
            </div>
          ) : (
            <div className="pk-card overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm responsive-table" data-testid="ce-table">
                  <thead className="bg-slate-50/80 border-b">
                    <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500">
                      <th className="px-4 py-3 font-semibold">Name</th>
                      <th className="px-4 py-3 font-semibold">Contact</th>
                      <th className="px-4 py-3 font-semibold">PAN / Aadhaar</th>
                      <th className="px-4 py-3 font-semibold">Rate</th>
                      <th className="px-4 py-3 font-semibold">Travel</th>
                      <th className="px-4 py-3 font-semibold text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredCEs.map((c) => {
                      const isInactive = c.is_active === false;
                      return (
                      <tr key={c.id} className={`border-b last:border-0 hover:bg-slate-50/60 ${isInactive ? "bg-rose-50/40" : ""}`} data-testid={`ce-row-${c.id}`}>
                        <td data-label="Name" className="px-4 py-3 font-semibold text-slate-800">
                          <div className="flex items-center gap-2 flex-wrap">
                            {c.name}
                            {isInactive && <Badge className="bg-rose-100 text-rose-800 text-[10px]" data-testid={`ce-inactive-${c.id}`}>INACTIVE</Badge>}
                          </div>
                        </td>
                        <td data-label="Contact" className="px-4 py-3 text-[12px] text-slate-600">
                          {c.email && <div>{c.email}</div>}
                          {c.mobile && <div className="mono">{c.mobile}</div>}
                          {!c.email && !c.mobile && <span className="text-slate-400">—</span>}
                        </td>
                        <td data-label="PAN / Aadhaar" className="px-4 py-3 mono text-[11px] text-slate-600">
                          {c.pan && <div>PAN {c.pan}</div>}
                          {c.aadhaar && <div>Aadhaar {c.aadhaar}</div>}
                          {!c.pan && !c.aadhaar && <span className="text-slate-400">—</span>}
                        </td>
                        <td data-label="Rate" className="px-4 py-3">
                          <Badge variant="secondary" className="text-[11px] mono">
                            {c.rate_type === "per_day" ? `₹ ${Number(c.rate_amount).toLocaleString("en-IN")}/day` :
                             c.rate_type === "per_session" ? `₹ ${Number(c.rate_per_session).toLocaleString("en-IN")}/session` :
                             `Fixed ₹ ${Number(c.rate_amount).toLocaleString("en-IN")}`}
                          </Badge>
                          {c.job_type && <div className="text-[10px] text-slate-500 mt-0.5">{c.job_type}</div>}
                        </td>
                        <td data-label="Travel" className="px-4 py-3 text-[11px]">
                          {!c.travel_needed ? (
                            <span className="text-slate-400">Not required</span>
                          ) : c.travel_mode === "fixed" ? (
                            <Badge className="bg-amber-100 text-amber-800">Fixed ₹ {Number(c.travel_amount).toLocaleString("en-IN")}</Badge>
                          ) : (
                            <Badge className="bg-sky-100 text-sky-800">Variable</Badge>
                          )}
                          {c.travel_remark && <div className="text-[10px] text-slate-500 mt-0.5 italic">{c.travel_remark}</div>}
                        </td>
                        <td data-label="Actions" className="px-4 py-3 text-right space-x-1">
                          <Button size="sm" variant="outline" onClick={() => openCELogs(c)} data-testid={`ce-logs-${c.id}`} title="View Logs">
                            <ScrollText size={11} />
                          </Button>
                          <Button
                            size="sm" variant="outline"
                            className={isInactive ? "text-emerald-700 border-emerald-200 hover:bg-emerald-50" : "text-amber-700 border-amber-200 hover:bg-amber-50"}
                            onClick={() => toggleCEActive(c)}
                            data-testid={`ce-toggle-${c.id}`}
                            title={isInactive ? "Mark as Active" : "Mark as Inactive"}
                          >
                            <Power size={11} className="mr-1" /> {isInactive ? "Activate" : "Inactivate"}
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => openEditCE(c)} data-testid={`ce-edit-${c.id}`}>
                            <Pencil size={11} className="mr-1" /> Edit
                          </Button>
                          <Button size="sm" variant="outline" className="text-rose-600 hover:bg-rose-50 border-rose-200" onClick={() => deleteCE(c.id)} data-testid={`ce-delete-${c.id}`}>
                            <Trash size={11} />
                          </Button>
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* =================== Contractual Employee Add/Edit Dialog =================== */}
      <Dialog open={showCEForm} onOpenChange={(o) => { if (!o) { setShowCEForm(false); setEditingCE(null); } }}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus size={16} className="text-violet-600" />
              {editingCE ? "Edit Contractual Employee" : "Add Contractual Employee"}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-3 py-2">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <div>
                <Label className="text-[10px] uppercase font-semibold text-slate-500">Name *</Label>
                <Input value={ceForm.name} onChange={(e) => setCEForm({ ...ceForm, name: e.target.value })} placeholder="Full name" data-testid="ce-name" />
              </div>
              <div>
                <Label className="text-[10px] uppercase font-semibold text-slate-500">Email</Label>
                <Input value={ceForm.email} onChange={(e) => setCEForm({ ...ceForm, email: e.target.value })} placeholder="email@example.com" data-testid="ce-email" />
              </div>
              <div>
                <Label className="text-[10px] uppercase font-semibold text-slate-500">Mobile</Label>
                <Input value={ceForm.mobile} onChange={(e) => setCEForm({ ...ceForm, mobile: e.target.value })} placeholder="10-digit number" className="mono" data-testid="ce-mobile" />
              </div>
              <div>
                <Label className="text-[10px] uppercase font-semibold text-slate-500">PAN</Label>
                <Input value={ceForm.pan} onChange={(e) => setCEForm({ ...ceForm, pan: e.target.value.toUpperCase() })} placeholder="ABCDE1234F" className="mono" data-testid="ce-pan" />
              </div>
              <div className="md:col-span-2">
                <Label className="text-[10px] uppercase font-semibold text-slate-500">Aadhaar</Label>
                <Input value={ceForm.aadhaar} onChange={(e) => setCEForm({ ...ceForm, aadhaar: e.target.value })} placeholder="12-digit Aadhaar" className="mono" data-testid="ce-aadhaar" />
              </div>
              <div className="md:col-span-2">
                <Label className="text-[10px] uppercase font-semibold text-slate-500">Job Type</Label>
                <Input value={ceForm.job_type} onChange={(e) => setCEForm({ ...ceForm, job_type: e.target.value })} placeholder="e.g. Trainer, Coach, Mentor" data-testid="ce-job-type" />
              </div>
              <div className="md:col-span-2">
                <Label className="text-[10px] uppercase font-semibold text-slate-500">Address</Label>
                <Textarea
                  value={ceForm.address}
                  onChange={(e) => setCEForm({ ...ceForm, address: e.target.value })}
                  placeholder="Full residential / correspondence address"
                  rows={2}
                  data-testid="ce-address"
                />
              </div>
              <div>
                <Label className="text-[10px] uppercase font-semibold text-slate-500">Bank Account No.</Label>
                <Input value={ceForm.bank_account_no} onChange={(e) => setCEForm({ ...ceForm, bank_account_no: e.target.value })} placeholder="e.g. 1234567890" className="mono" data-testid="ce-bank-acc" />
              </div>
              <div>
                <Label className="text-[10px] uppercase font-semibold text-slate-500">IFSC Code</Label>
                <Input value={ceForm.ifsc} onChange={(e) => setCEForm({ ...ceForm, ifsc: e.target.value.toUpperCase() })} placeholder="HDFC0001234" className="mono" data-testid="ce-ifsc" />
              </div>
            </div>

            {/* Master-admin-only: account-added flag */}
            {isMasterAdmin && (
              <div className="p-3 border rounded-md bg-violet-50/40 border-violet-200 space-y-2" data-testid="ce-account-block">
                <div className="flex items-center gap-2 flex-wrap">
                  <Label className="text-[11px] uppercase tracking-wider font-semibold text-violet-800 flex-1 flex items-center gap-2">
                    Account Added in Books? <Badge className="bg-amber-100 text-amber-800 text-[9px]">MASTER ADMIN</Badge>
                  </Label>
                  {[true, false].map((v) => (
                    <button key={String(v)} type="button"
                      onClick={() => setCEForm({ ...ceForm, is_account_added: v, company_account_name: v ? ceForm.company_account_name : "" })}
                      className={`px-3 py-1 text-[11px] font-semibold rounded-full border ${ceForm.is_account_added === v ? (v ? "bg-emerald-100 border-emerald-300 text-emerald-800" : "bg-slate-200 border-slate-300 text-slate-700") : "bg-white border-slate-200 text-slate-600"}`}
                      data-testid={`ce-acc-added-${v ? "yes" : "no"}`}>
                      {v ? "Yes" : "No"}
                    </button>
                  ))}
                </div>
                {ceForm.is_account_added && (
                  <div>
                    <Label className="text-[10px] uppercase font-semibold text-slate-500">In Which Company Account?</Label>
                    <Input
                      value={ceForm.company_account_name}
                      onChange={(e) => setCEForm({ ...ceForm, company_account_name: e.target.value })}
                      placeholder="e.g. Panchakosh Ventures Pvt Ltd"
                      data-testid="ce-company-acc"
                    />
                  </div>
                )}
              </div>
            )}

            {/* Rate */}
            <div className="p-3 border rounded-md bg-slate-50/60 space-y-2">
              <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">Rate</Label>
              <div className="flex items-center gap-1.5 flex-wrap">
                {[["per_day", "Per Day"], ["per_session", "Per Session"], ["fixed", "Fixed for Term"]].map(([v, l]) => (
                  <button key={v} type="button" onClick={() => setCEForm({ ...ceForm, rate_type: v })}
                    className={`flex-1 min-w-[100px] h-9 text-[12px] font-semibold rounded border ${ceForm.rate_type === v ? "bg-[var(--pk-accent)] border-[var(--pk-accent)] text-white" : "bg-white border-slate-200 text-slate-600"}`}
                    data-testid={`ce-rate-${v}`}>{l}</button>
                ))}
              </div>
              {ceForm.rate_type === "per_session" ? (
                <Input
                  type="number" min={0} step="0.01"
                  placeholder="Amount per session (₹)"
                  value={ceForm.rate_per_session}
                  onChange={(e) => setCEForm({ ...ceForm, rate_per_session: e.target.value })}
                  className="mono"
                  data-testid="ce-rate-per-session"
                />
              ) : (
                <Input
                  type="number" min={0} step="0.01"
                  placeholder={ceForm.rate_type === "per_day" ? "Amount per day (₹)" : "Total amount for full term (₹)"}
                  value={ceForm.rate_amount}
                  onChange={(e) => setCEForm({ ...ceForm, rate_amount: e.target.value })}
                  className="mono"
                  data-testid="ce-rate-amount"
                />
              )}
            </div>

            {/* Travel */}
            <div className="p-3 border rounded-md bg-slate-50/60 space-y-2">
              <div className="flex items-center gap-2 flex-wrap">
                <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600 flex-1">Do we need to provide Travel?</Label>
                {[true, false].map((v) => (
                  <button key={String(v)} type="button" onClick={() => setCEForm({ ...ceForm, travel_needed: v })}
                    className={`px-3 py-1 text-[11px] font-semibold rounded-full border ${ceForm.travel_needed === v ? (v ? "bg-emerald-100 border-emerald-300 text-emerald-800" : "bg-slate-200 border-slate-300 text-slate-700") : "bg-white border-slate-200 text-slate-600"}`}
                    data-testid={`ce-travel-${v ? "yes" : "no"}`}>{v ? "Yes" : "No"}</button>
                ))}
              </div>

              {ceForm.travel_needed && (
                <>
                  <div>
                    <Label className="text-[10px] uppercase font-semibold text-slate-500">Travel Remark</Label>
                    <Textarea
                      value={ceForm.travel_remark}
                      onChange={(e) => setCEForm({ ...ceForm, travel_remark: e.target.value })}
                      placeholder="e.g. Round-trip from Delhi to Mumbai, 2 nights stay"
                      rows={2}
                      data-testid="ce-travel-remark"
                    />
                  </div>

                  <div className="flex items-center gap-1.5">
                    {[["fixed", "Fixed Amount"], ["variable", "Variable"]].map(([v, l]) => (
                      <button key={v} type="button" onClick={() => setCEForm({ ...ceForm, travel_mode: v })}
                        className={`flex-1 h-9 text-[12px] font-semibold rounded border ${ceForm.travel_mode === v ? "bg-[var(--pk-accent)] border-[var(--pk-accent)] text-white" : "bg-white border-slate-200 text-slate-600"}`}
                        data-testid={`ce-travel-mode-${v}`}>{l}</button>
                    ))}
                  </div>

                  {ceForm.travel_mode === "fixed" ? (
                    <div>
                      <Label className="text-[10px] uppercase font-semibold text-slate-500">Fixed Travel Amount (₹)</Label>
                      <Input
                        type="number" min={0} step="0.01"
                        placeholder="e.g. 5000"
                        value={ceForm.travel_amount}
                        onChange={(e) => setCEForm({ ...ceForm, travel_amount: e.target.value })}
                        className="mono"
                        data-testid="ce-travel-amount"
                      />
                    </div>
                  ) : (
                    <div>
                      <Label className="text-[10px] uppercase font-semibold text-slate-500">Variable Travel — Option Remark</Label>
                      <Textarea
                        value={ceForm.travel_variable_remark}
                        onChange={(e) => setCEForm({ ...ceForm, travel_variable_remark: e.target.value })}
                        placeholder="e.g. Reimburse actuals on submission of bills"
                        rows={2}
                        data-testid="ce-travel-variable-remark"
                      />
                    </div>
                  )}
                </>
              )}
            </div>

            <div>
              <Label className="text-[10px] uppercase font-semibold text-slate-500">Remark (optional)</Label>
              <Textarea
                value={ceForm.remark}
                onChange={(e) => setCEForm({ ...ceForm, remark: e.target.value })}
                placeholder="Any additional notes"
                rows={2}
                data-testid="ce-remark"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowCEForm(false); setEditingCE(null); }} disabled={ceSaving}>Cancel</Button>
            <Button onClick={saveCE} disabled={ceSaving} data-testid="ce-save">
              {ceSaving ? "Saving…" : (editingCE ? "Update Employee" : "Save Employee")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* =================== Order Detail Dialog =================== */}
      <Dialog open={!!detailOrder} onOpenChange={(o) => { if (!o) { setDetailOrder(null); setShowEmpForm(false); } }}>
        <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto">
          {detailOrder && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <ClipboardList size={18} className="text-violet-600" />
                  {detailOrder.order_id || "Service Order"}
                  <Badge className="bg-sky-100 text-sky-800">{detailOrder.client_name}</Badge>
                </DialogTitle>
              </DialogHeader>

              <Tabs defaultValue="overview" className="w-full">
                <TabsList>
                  <TabsTrigger value="overview" data-testid="so-tab-overview">Overview</TabsTrigger>
                  <TabsTrigger value="employees" data-testid="so-tab-employees">
                    Deployed Employees · {(detailOrder.contractual_employees || []).length}
                  </TabsTrigger>
                  <TabsTrigger value="calculate" data-testid="so-tab-calculate">
                    <Calculator size={12} className="mr-1" /> Calculate
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="overview" className="space-y-3 mt-3">
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <KV label="Client" value={detailOrder.client_name} />
                    <KV label="Invoice Company" value={detailOrder.invoice_company_name} />
                    <KV label="Service" value={detailOrder.product_service} />
                    <KV label="Total Value" value={`₹ ${Number(detailOrder.total_value || 0).toLocaleString("en-IN")}`} />
                    <KV label="Sale Associate" value={detailOrder.sale_associate_name} />
                    <KV label="Commission" value={detailOrder.commission} />
                  </div>
                  {detailOrder.remark && <KV label="Remark" value={detailOrder.remark} block />}
                </TabsContent>

                <TabsContent value="employees" className="space-y-3 mt-3">
                  <div className="flex items-center justify-between">
                    <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">Employees deployed on this order</Label>
                    <Button size="sm" onClick={() => { setEmpForm(EMPTY_ORDER_EMP); setShowEmpForm(true); }} data-testid="so-add-emp-btn">
                      <Plus size={12} className="mr-1" /> Add Employee
                    </Button>
                  </div>

                  {showEmpForm && (
                    <div className="p-4 border rounded-md bg-slate-50/60 space-y-3" data-testid="so-emp-form">
                      {ces.filter((c) => c.is_active !== false).length > 0 && (
                        <div>
                          <Label className="text-[10px] uppercase font-semibold text-slate-500">Pick from Directory (Active only)</Label>
                          <select
                            className="mt-1 w-full h-9 border rounded text-[12px] px-2 bg-white"
                            onChange={(e) => {
                              const c = ces.find((x) => x.id === e.target.value);
                              if (!c) return;
                              setEmpForm({
                                ...empForm,
                                directory_id: c.id,
                                name: c.name, email: c.email, mobile: c.mobile,
                                pan: c.pan, aadhaar: c.aadhaar,
                                job_type: c.job_type || "",
                                rate_type: c.rate_type,
                                rate_amount: c.rate_amount || 0,
                                rate_per_session: c.rate_per_session || 0,
                                travel_needed: c.travel_needed,
                                travel_mode: c.travel_mode || "fixed",
                                travel_amount: c.travel_amount || 0,
                                travel_remark: c.travel_remark || "",
                              });
                            }}
                            data-testid="so-emp-pick"
                          >
                            <option value="">— Pick existing contractual employee —</option>
                            {ces.filter((c) => c.is_active !== false).map((c) => <option key={c.id} value={c.id}>{c.name}{c.job_type ? ` · ${c.job_type}` : ""}{c.mobile ? ` · ${c.mobile}` : ""}</option>)}
                          </select>
                        </div>
                      )}

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        <Input placeholder="Name *" value={empForm.name} onChange={(e) => setEmpForm({ ...empForm, name: e.target.value })} data-testid="emp-name" />
                        <Input placeholder="Email" value={empForm.email} onChange={(e) => setEmpForm({ ...empForm, email: e.target.value })} data-testid="emp-email" />
                        <Input placeholder="Mobile" value={empForm.mobile} onChange={(e) => setEmpForm({ ...empForm, mobile: e.target.value })} data-testid="emp-mobile" />
                        <Input placeholder="PAN" value={empForm.pan} onChange={(e) => setEmpForm({ ...empForm, pan: e.target.value.toUpperCase() })} className="mono" data-testid="emp-pan" />
                        <Input placeholder="Aadhaar" value={empForm.aadhaar} onChange={(e) => setEmpForm({ ...empForm, aadhaar: e.target.value })} className="mono" data-testid="emp-aadhaar" />
                        <Input placeholder="Job Type" value={empForm.job_type} onChange={(e) => setEmpForm({ ...empForm, job_type: e.target.value })} data-testid="emp-job-type" />
                      </div>

                      <Textarea
                        placeholder="Service Order Remark"
                        value={empForm.service_order_remark}
                        onChange={(e) => setEmpForm({ ...empForm, service_order_remark: e.target.value })}
                        rows={2}
                        data-testid="emp-so-remark"
                      />

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-2 items-end">
                        <div>
                          <Label className="text-[10px] uppercase font-semibold text-slate-500">Rate Type</Label>
                          <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                            {[["per_day", "Per Day"], ["per_session", "Per Session"], ["fixed", "Fixed"]].map(([v, l]) => (
                              <button key={v} type="button" onClick={() => setEmpForm({ ...empForm, rate_type: v })}
                                className={`flex-1 min-w-[70px] h-9 text-[11px] font-semibold rounded border ${empForm.rate_type === v ? "bg-[var(--pk-accent)] border-[var(--pk-accent)] text-white" : "bg-white border-slate-200 text-slate-600"}`}
                                data-testid={`emp-rate-${v}`}>{l}</button>
                            ))}
                          </div>
                        </div>
                        {empForm.rate_type === "per_session" ? (
                          <Input type="number" min={0} step="0.01" placeholder="Per session amount" value={empForm.rate_per_session} onChange={(e) => setEmpForm({ ...empForm, rate_per_session: e.target.value })} className="mono" data-testid="emp-rate-per-session" />
                        ) : (
                          <Input type="number" min={0} step="0.01" placeholder={empForm.rate_type === "per_day" ? "Amount per day" : "Total amount"} value={empForm.rate_amount} onChange={(e) => setEmpForm({ ...empForm, rate_amount: e.target.value })} className="mono" data-testid="emp-rate-amount" />
                        )}
                        <div>
                          <Label className="text-[10px] uppercase font-semibold text-slate-500">Sessions / Day (Committed)</Label>
                          <Input type="number" min={0} step="0.5" value={empForm.sessions_per_day_committed} onChange={(e) => setEmpForm({ ...empForm, sessions_per_day_committed: e.target.value })} className="mono" data-testid="emp-sess-per-day" />
                        </div>
                      </div>

                      <div className="p-3 border rounded-md bg-white space-y-2">
                        <div className="flex items-center gap-2">
                          <Label className="text-[11px] font-semibold text-slate-700 flex-1">Do we need to provide Travel?</Label>
                          {[true, false].map((v) => (
                            <button key={String(v)} type="button" onClick={() => setEmpForm({ ...empForm, travel_needed: v })}
                              className={`px-3 py-1 text-[11px] font-semibold rounded-full border ${empForm.travel_needed === v ? (v ? "bg-emerald-100 border-emerald-300 text-emerald-800" : "bg-slate-200 border-slate-300 text-slate-700") : "bg-white border-slate-200 text-slate-600"}`}
                              data-testid={`emp-travel-${v ? "yes" : "no"}`}>{v ? "Yes" : "No"}</button>
                          ))}
                        </div>
                        {empForm.travel_needed && (
                          <>
                            <div className="flex items-center gap-1.5">
                              {[["fixed", "Fixed Amount"], ["variable", "Variable"]].map(([v, l]) => (
                                <button key={v} type="button" onClick={() => setEmpForm({ ...empForm, travel_mode: v })}
                                  className={`flex-1 h-8 text-[11px] font-semibold rounded border ${empForm.travel_mode === v ? "bg-[var(--pk-accent)] border-[var(--pk-accent)] text-white" : "bg-white border-slate-200 text-slate-600"}`}
                                  data-testid={`emp-travel-mode-${v}`}>{l}</button>
                              ))}
                            </div>
                            {empForm.travel_mode === "fixed" ? (
                              <Input type="number" min={0} step="0.01" placeholder="Travel amount ₹" value={empForm.travel_amount} onChange={(e) => setEmpForm({ ...empForm, travel_amount: e.target.value })} className="mono" data-testid="emp-travel-amount" />
                            ) : (
                              <Textarea placeholder="Travel remark (e.g. reimburse actuals on bills)" value={empForm.travel_remark} onChange={(e) => setEmpForm({ ...empForm, travel_remark: e.target.value })} rows={2} data-testid="emp-travel-remark" />
                            )}
                          </>
                        )}
                      </div>

                      <div className="grid grid-cols-2 md:grid-cols-3 gap-2 p-3 border rounded bg-slate-50/40">
                        <div className="md:col-span-3">
                          <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">Schedule Dates</Label>
                        </div>
                        <div>
                          <Label className="text-[10px] uppercase font-semibold text-slate-500">In School — From</Label>
                          <Input type="date" value={empForm.school_from} onChange={(e) => setEmpForm({ ...empForm, school_from: e.target.value })} className="mt-1" data-testid="emp-school-from" />
                        </div>
                        <div>
                          <Label className="text-[10px] uppercase font-semibold text-slate-500">In School — To</Label>
                          <Input type="date" value={empForm.school_to} onChange={(e) => setEmpForm({ ...empForm, school_to: e.target.value })} className="mt-1" data-testid="emp-school-to" />
                        </div>
                        <div>
                          <Label className="text-[10px] uppercase font-semibold text-slate-500">Travel Start</Label>
                          <Input type="date" value={empForm.travel_start_date} onChange={(e) => setEmpForm({ ...empForm, travel_start_date: e.target.value })} className="mt-1" data-testid="emp-travel-start" />
                        </div>
                        <div>
                          <Label className="text-[10px] uppercase font-semibold text-slate-500">Trainer Reach</Label>
                          <Input type="date" value={empForm.trainer_reach_date} onChange={(e) => setEmpForm({ ...empForm, trainer_reach_date: e.target.value })} className="mt-1" data-testid="emp-trainer-reach" />
                        </div>
                        <div>
                          <Label className="text-[10px] uppercase font-semibold text-slate-500">Trainer Free</Label>
                          <Input type="date" value={empForm.trainer_free_date} onChange={(e) => setEmpForm({ ...empForm, trainer_free_date: e.target.value })} className="mt-1" data-testid="emp-trainer-free" />
                        </div>
                      </div>

                      <Textarea placeholder="Remark (optional)" value={empForm.remark} onChange={(e) => setEmpForm({ ...empForm, remark: e.target.value })} rows={2} data-testid="emp-remark" />

                      <div className="flex justify-end gap-2 pt-2">
                        <Button variant="outline" size="sm" onClick={() => setShowEmpForm(false)} disabled={saving}>Cancel</Button>
                        <Button size="sm" onClick={addOrderEmployee} disabled={saving} data-testid="emp-save">{saving ? "Saving…" : "Add Employee"}</Button>
                      </div>
                    </div>
                  )}

                  {(detailOrder.contractual_employees || []).length === 0 && !showEmpForm ? (
                    <div className="p-6 text-center text-slate-400 text-[12px] border border-dashed rounded">No employees deployed on this order yet.</div>
                  ) : (
                    <div className="space-y-2">
                      {(detailOrder.contractual_employees || []).map((emp) => {
                        const empInactive = emp.is_active === false;
                        const linkedDir = emp.directory_id ? ces.find((c) => c.id === emp.directory_id) : null;
                        const noActiveUser = linkedDir && linkedDir.is_active === false;
                        return (
                        <div key={emp.id} className={`p-3 border rounded-md bg-white ${empInactive ? "opacity-70 border-rose-200" : ""}`} data-testid={`emp-card-${emp.id}`}>
                          <div className="flex items-start justify-between flex-wrap gap-2">
                            <div>
                              <div className="font-semibold text-slate-800 flex items-center gap-2 flex-wrap">
                                {emp.name}
                                {emp.job_type && <Badge variant="outline" className="text-[10px]">{emp.job_type}</Badge>}
                                {empInactive && <Badge className="bg-rose-100 text-rose-800 text-[10px]" data-testid={`emp-inactive-${emp.id}`}>INACTIVE</Badge>}
                                {noActiveUser && !empInactive && <Badge className="bg-amber-100 text-amber-800 text-[10px]" data-testid={`emp-no-active-${emp.id}`}>NO ACTIVE USER</Badge>}
                              </div>
                              <div className="text-[11px] text-slate-500 flex flex-wrap gap-2 mt-0.5">
                                {emp.email && <span>{emp.email}</span>}
                                {emp.mobile && <span className="mono">{emp.mobile}</span>}
                                {emp.pan && <span className="mono">PAN {emp.pan}</span>}
                                {emp.aadhaar && <span className="mono">Aadhaar {emp.aadhaar}</span>}
                              </div>
                            </div>
                            {empInactive ? (
                              <Badge className="bg-slate-100 text-slate-500 text-[10px]">Locked</Badge>
                            ) : (
                              <button
                                onClick={() => setMarkInactive({ emp, did_sessions: false, reason: "" })}
                                className="h-7 px-2 inline-flex items-center justify-center text-rose-600 hover:bg-rose-50 rounded text-[11px] font-semibold gap-1 border border-rose-200"
                                data-testid={`emp-mark-inactive-${emp.id}`}
                                title="Mark Inactive for this order"
                              >
                                <UserX size={12} /> Mark Inactive
                              </button>
                            )}
                          </div>
                          {emp.service_order_remark && (
                            <div className="mt-1 text-[12px] text-slate-700 italic bg-slate-50 px-2 py-1 rounded">
                              &ldquo;{emp.service_order_remark}&rdquo;
                            </div>
                          )}
                          <div className="mt-2 flex flex-wrap gap-2 text-[11px]">
                            <Badge variant="secondary">
                              {emp.rate_type === "per_day" ? `₹ ${emp.rate_amount}/day` :
                               emp.rate_type === "per_session" ? `₹ ${emp.rate_per_session}/session` :
                               `Fixed ₹ ${emp.rate_amount}`}
                            </Badge>
                            {emp.sessions_per_day_committed > 0 && <Badge className="bg-sky-100 text-sky-800">{emp.sessions_per_day_committed} sess/day</Badge>}
                            {emp.travel_needed && (
                              <Badge className="bg-amber-100 text-amber-800">
                                Travel · {emp.travel_mode === "fixed" ? `₹ ${emp.travel_amount}` : "Variable"}
                              </Badge>
                            )}
                            {emp.school_from && <Badge variant="outline">🏫 From {emp.school_from}</Badge>}
                            {emp.school_to && <Badge variant="outline">🏫 To {emp.school_to}</Badge>}
                            {emp.travel_start_date && <Badge variant="outline">✈ Travel {emp.travel_start_date}</Badge>}
                            {emp.trainer_reach_date && <Badge variant="outline">📍 Reach {emp.trainer_reach_date}</Badge>}
                            {emp.trainer_free_date && <Badge variant="outline">🏁 Free {emp.trainer_free_date}</Badge>}
                            {emp.service_order_no && <Badge className="bg-violet-100 text-violet-800 mono">{emp.service_order_no}</Badge>}
                          </div>
                          {emp.remark && <div className="mt-1 text-[11px] text-slate-500">{emp.remark}</div>}
                          {emp.logs?.length > 0 && (
                            <div className="mt-2 pt-2 border-t">
                              <div className="text-[10px] uppercase font-semibold text-slate-500 mb-1">Activity Log</div>
                              <div className="space-y-0.5">
                                {emp.logs.slice(-5).reverse().map((lg) => (
                                  <div key={lg.id} className="text-[10px] text-slate-600 flex items-start gap-1.5">
                                    <span className="mono text-slate-400">{(lg.at || "").slice(0, 16).replace("T", " ")}</span>
                                    <span className="font-semibold">{lg.by_name}:</span>
                                    <span>{lg.note}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                        );
                      })}
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="calculate" className="mt-3">
                  <ServiceOrderCalculation order={detailOrder} isMasterAdmin={isMasterAdmin} onClose={() => {}} />
                </TabsContent>
              </Tabs>

              <DialogFooter>
                <Button variant="outline" onClick={() => setDetailOrder(null)}>Close</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* =================== Log Viewer (orders + CE) =================== */}
      <LogViewerDialog
        open={logViewer.open}
        onClose={() => setLogViewer({ open: false, title: "", endpoint: "" })}
        title={logViewer.title}
        endpoint={logViewer.endpoint}
      />

      {/* =================== Mark Inactive for Order Dialog =================== */}
      <Dialog open={!!markInactive} onOpenChange={(o) => { if (!o) setMarkInactive(null); }}>
        <DialogContent className="max-w-md">
          {markInactive && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <AlertTriangle size={16} className="text-amber-600" />
                  Mark Inactive for Order
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-3 py-2">
                <div className="p-3 bg-amber-50 border border-amber-200 rounded text-[12.5px] text-amber-900">
                  Once marked inactive, <strong>{markInactive.emp.name}</strong> cannot be reactivated for this order but their record stays for audit.
                </div>
                <div>
                  <Label className="text-[11px] font-semibold text-slate-700">Did this employee perform any sessions on this order?</Label>
                  <div className="flex items-center gap-2 mt-2">
                    {[true, false].map((v) => (
                      <button key={String(v)} type="button"
                        onClick={() => setMarkInactive({ ...markInactive, did_sessions: v })}
                        className={`flex-1 h-10 text-[12px] font-semibold rounded border ${markInactive.did_sessions === v ? (v ? "bg-emerald-100 border-emerald-300 text-emerald-800" : "bg-slate-200 border-slate-300 text-slate-700") : "bg-white border-slate-200 text-slate-600"}`}
                        data-testid={`mark-inactive-did-${v ? "yes" : "no"}`}>
                        {v ? "Yes — calculate salary" : "No — just inactivate"}
                      </button>
                    ))}
                  </div>
                  <p className="text-[10.5px] text-slate-500 mt-1.5">
                    {markInactive.did_sessions
                      ? "We'll open the Calculate tab so you can compute their salary for sessions already performed."
                      : "No further action needed — the order will show this person as inactive."}
                  </p>
                </div>
                <div>
                  <Label className="text-[11px] font-semibold text-slate-700">Reason (optional)</Label>
                  <Textarea
                    value={markInactive.reason}
                    onChange={(e) => setMarkInactive({ ...markInactive, reason: e.target.value })}
                    placeholder="e.g. Withdrew due to personal reasons / replaced by another trainer"
                    rows={2}
                    data-testid="mark-inactive-reason"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setMarkInactive(null)}>Cancel</Button>
                <Button onClick={confirmMarkInactive} className="bg-rose-600 hover:bg-rose-700" data-testid="mark-inactive-confirm">
                  Confirm Inactive
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* =================== Direct For Invoice Dialog =================== */}
      <Dialog open={!!directInvoice} onOpenChange={(o) => { if (!o) setDirectInvoice(null); }}>
        <DialogContent className="max-w-md">
          {directInvoice && (
            <>
              <DialogHeader>
                <DialogTitle className="heading flex items-center gap-2">
                  Direct For Invoice — {directInvoice.order.order_id || directInvoice.order.client_name}
                </DialogTitle>
                <div className="text-[11px] text-slate-500">Skip session calculation and send a flat amount to Accounts for invoicing.</div>
              </DialogHeader>
              <div className="space-y-3 py-2">
                <div>
                  <Label className="text-[10px] uppercase font-semibold text-slate-500">Month *</Label>
                  <Input type="month" value={directInvoice.month_key} onChange={(e) => setDirectInvoice({ ...directInvoice, month_key: e.target.value })} data-testid="direct-month" />
                </div>
                <div>
                  <Label className="text-[10px] uppercase font-semibold text-slate-500">Invoice Amount (₹) *</Label>
                  <Input type="number" min={0} step="0.01" value={directInvoice.amount} onChange={(e) => setDirectInvoice({ ...directInvoice, amount: e.target.value })} className="mono" placeholder="e.g. 120000" data-testid="direct-amount" />
                </div>
                <div>
                  <Label className="text-[10px] uppercase font-semibold text-slate-500">Notes (optional)</Label>
                  <Textarea rows={2} value={directInvoice.notes} onChange={(e) => setDirectInvoice({ ...directInvoice, notes: e.target.value })} placeholder="e.g. Full payment due as per agreement" data-testid="direct-notes" />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setDirectInvoice(null)}>Cancel</Button>
                <Button onClick={submitDirectInvoice} disabled={savingDirect} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="direct-submit">
                  {savingDirect ? "Submitting…" : "Send to Accounts"}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function KV({ label, value, block }) {
  return (
    <div className={block ? "col-span-2" : ""}>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
      <div className="text-slate-800">{value || "—"}</div>
    </div>
  );
}
